import { useState } from 'react';
import { useProjectStore } from '../../stores/useProjectStore';
import type { ProjectFile, ProjectSource } from '../../types/project';
import styles from './Modal.module.css';

interface CreateProjectModalProps {
  onClose: () => void;
  onCreated: (projectId: string) => Promise<void>;
  prefabMode?: boolean;
}

interface Template {
  id: string;
  name: string;
  description: string;
  files: ProjectFile[];
}

function makeId(): string {
  return crypto.randomUUID();
}

const TEMPLATES: Template[] = [
  {
    id: 'token',
    name: 'Token Contract',
    description: 'Basic fungible token with mint/transfer/burn.',
    files: [
      {
        id: makeId(), name: 'token.mist', path: 'src/token.mist',
        content: '# Rivellum Token Contract\n# Fungible token with mint, transfer, and burn.\n\ncontract Token\n    state total_supply: Int\n    state owner: Address\n\n    function init(initial_supply: Int)\n        require initial_supply > 0\n        owner = caller\n        total_supply = initial_supply\n        emit Initialized(owner, initial_supply)\n\n    function mint(amount: Int)\n        require caller == owner\n        require amount > 0\n        total_supply = total_supply + amount\n        emit Minted(caller, amount)\n\n    function burn(amount: Int)\n        require amount > 0\n        require total_supply >= amount\n        total_supply = total_supply - amount\n        emit Burned(caller, amount)\n\n    function get_supply() -> Int\n        return total_supply\n',
        language: 'mist', isModified: false,
      },
      {
        id: makeId(), name: 'rivellum.toml', path: 'rivellum.toml',
        content: '[project]\nname = "token-project"\nversion = "0.1.0"\nnetwork = "testnet"\n\n[build]\nentry = "src/token.mist"\n',
        language: 'toml', isModified: false,
      },
    ],
  },
  {
    id: 'nft',
    name: 'NFT Collection',
    description: 'Non-fungible token collection with metadata.',
    files: [
      {
        id: makeId(), name: 'nft.mist', path: 'src/nft.mist',
        content: '# Rivellum NFT Collection\n# Non-fungible token collection with metadata.\n\ncontract NFTCollection\n    state next_id: Int\n    state owner: Address\n    state total_minted: Int\n\n    function init()\n        next_id = 0\n        owner = caller\n        total_minted = 0\n        emit CollectionCreated(owner)\n\n    function mint(name: String, uri: String)\n        require caller == owner\n        let token_id = next_id\n        next_id = next_id + 1\n        total_minted = total_minted + 1\n        emit NFTMinted(token_id, name, uri, caller)\n\n    function get_total_minted() -> Int\n        return total_minted\n',
        language: 'mist', isModified: false,
      },
      {
        id: makeId(), name: 'rivellum.toml', path: 'rivellum.toml',
        content: '[project]\nname = "nft-collection"\nversion = "0.1.0"\nnetwork = "testnet"\n\n[build]\nentry = "src/nft.mist"\n',
        language: 'toml', isModified: false,
      },
    ],
  },
  {
    id: 'defi',
    name: 'DeFi Vault',
    description: 'Simple vault with deposit/withdraw and encrypted intents.',
    files: [
      {
        id: makeId(), name: 'vault.mist', path: 'src/vault.mist',
        content: '# Rivellum DeFi Vault\n# Encrypted intent submission with post-admission ordering.\n\ncontract Vault\n    state total_deposited: Int\n    state owner: Address\n\n    function init()\n        total_deposited = 0\n        owner = caller\n        emit VaultCreated(owner)\n\n    function deposit(amount: Int)\n        require amount > 0\n        total_deposited = total_deposited + amount\n        emit Deposited(caller, amount)\n\n    function withdraw(amount: Int)\n        require amount > 0\n        require total_deposited >= amount\n        total_deposited = total_deposited - amount\n        emit Withdrawn(caller, amount)\n\n    function get_balance() -> Int\n        return total_deposited\n',
        language: 'mist', isModified: false,
      },
      {
        id: makeId(), name: 'rivellum.toml', path: 'rivellum.toml',
        content: '[project]\nname = "defi-vault"\nversion = "0.1.0"\nnetwork = "testnet"\n\n[build]\nentry = "src/vault.mist"\n',
        language: 'toml', isModified: false,
      },
    ],
  },
  {
    id: 'game',
    name: 'Game Prefab',
    description: 'Game state with sessions, leaderboards, and rewards.',
    files: [
      {
        id: makeId(), name: 'game.mist', path: 'src/game.mist',
        content: '# Rivellum Game Prefab\n# Game session tracking with scores and rewards.\n\ncontract GameSession\n    state high_score: Int\n    state games_played: Int\n    state owner: Address\n    state active: Bool\n\n    function init()\n        high_score = 0\n        games_played = 0\n        owner = caller\n        active = true\n        emit SessionCreated(owner)\n\n    function submit_score(score: Int)\n        require active == true\n        require score > 0\n        games_played = games_played + 1\n        if score > high_score\n            high_score = score\n        emit ScoreSubmitted(caller, score, games_played)\n\n    function end_session()\n        require caller == owner\n        active = false\n        emit SessionEnded(owner, high_score, games_played)\n\n    function get_high_score() -> Int\n        return high_score\n',
        language: 'mist', isModified: false,
      },
      {
        id: makeId(), name: 'rivellum.toml', path: 'rivellum.toml',
        content: '[project]\nname = "game-prefab"\nversion = "0.1.0"\nnetwork = "testnet"\n\n[build]\nentry = "src/game.mist"\n',
        language: 'toml', isModified: false,
      },
    ],
  },
];

export function CreateProjectModal({ onClose, onCreated, prefabMode }: CreateProjectModalProps) {
  const [name, setName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const createProject = useProjectStore(s => s.createProject);

  const handleCreate = async () => {
    const projectName = name.trim();
    if (!projectName) {
      setError('Project name is required.');
      return;
    }
    setIsCreating(true);
    setError(null);

    try {
      const template = TEMPLATES.find(t => t.id === selectedTemplate);
      const files = template
        ? template.files.map(f => ({ ...f, id: crypto.randomUUID() }))
        : [];
      const source: ProjectSource = template ? 'prefab' : 'blank';
      const id = await createProject(projectName, source, files);
      await onCreated(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create project.');
      setIsCreating(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {prefabMode ? 'Deploy with Prefabs' : 'Create New Project'}
          </h2>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>
        <div className={styles.modalBody}>
          {error && <div className={styles.errorBanner}>{error}</div>}

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Project Name</label>
            <input
              className={styles.fieldInput}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="my-project"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Template</label>
            <div className={styles.templateGrid}>
              {TEMPLATES.map(t => (
                <button
                  key={t.id}
                  className={`${styles.templateCard} ${selectedTemplate === t.id ? styles.selected : ''}`}
                  onClick={() => setSelectedTemplate(prev => prev === t.id ? null : t.id)}
                >
                  <span className={styles.templateName}>{t.name}</span>
                  <span className={styles.templateDesc}>{t.description}</span>
                </button>
              ))}
            </div>
            <span className={styles.fieldHint}>
              {prefabMode
                ? 'Select a prefab template to scaffold your project.'
                : 'Optional — leave unselected for an empty project.'}
            </span>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button className={styles.submitBtn} onClick={handleCreate} disabled={isCreating}>
            {isCreating ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}
